import { z } from 'zod'
import { ExternalFolderLinkMetadataSchema } from '@/user_files/external-storage/schemas'
import type { FolderPathPrecondition, FolderRenamePrecondition } from '@/user_files/schemas/preconditions'

// ============================================================================
// Response Schemas (Zod - runtime validated)
// ============================================================================

/**
 * FolderResponse - single folder from backend
 * Used by: GET /{folder_id}, POST /, PATCH /{folder_id}, POST /restore
 */
export const FolderResponseSchema = z.object({
  id: z.string().uuid(),
  matter_id: z.string().uuid(),
  parent_id: z.string().uuid().nullable(),
  name: z.string(),
  path: z.string(), // Materialized path e.g., "/abc/def/ghi/"
  is_root: z.boolean(),
  is_deleted: z.boolean(),
  // Visible files in this folder and its active descendants, excluding soft-deleted files and
  // PENDING upload placeholders. Required and non-null on the backend
  // (`paxton_app/user_files/folders/schemas.py`), and the value the Size column sorts folders by.
  file_count: z.number().int().nonnegative(),
  created_at: z.string(), // ISO datetime
  updated_at: z.string(), // ISO datetime
  external_storage: ExternalFolderLinkMetadataSchema.nullable().optional(),
})

export type FolderResponse = z.infer<typeof FolderResponseSchema>

/**
 * RootFolderResponse - wrapped response for GET /root
 */
export const RootFolderResponseSchema = z.object({
  folder: FolderResponseSchema,
})

export type RootFolderResponse = z.infer<typeof RootFolderResponseSchema>

/**
 * FolderListResponse - list of children folders
 * Used by: GET /{folder_id}/children
 */
export const FolderListResponseSchema = z.object({
  folders: z.array(FolderResponseSchema),
  parent_id: z.string().uuid().nullable(),
  next_cursor: z.string().nullable(),
})

export type FolderListResponse = z.infer<typeof FolderListResponseSchema>

/**
 * API Error Response
 */
export const FolderApiErrorSchema = z.object({
  detail: z.unknown(),
})

export type FolderApiError = z.infer<typeof FolderApiErrorSchema>

// ============================================================================
// Request Types (TypeScript only - no runtime validation needed)
// ============================================================================

/**
 * CreateFolderRequest - POST /
 */
export interface CreateFolderRequest {
  parent_id: string // UUID of parent folder
  name: string // 1-255 characters
  parent_folder_precondition?: FolderPathPrecondition
}

/**
 * RenameFolderRequest - PATCH /{folder_id}
 */
export interface RenameFolderRequest {
  name: string // 1-255 characters
  precondition?: FolderRenamePrecondition
}
